import {
  WarningRounded,
  FiberManualRecord,
  AccountCircleOutlined,
} from "@material-ui/icons";
import { Badge } from "@material-ui/core";
import { compose } from "recompose";
import { pluralize } from "../util";
import { format } from "date-fns";
import reportConstants from "../constants"
import colors from "../styleColors";
import Print from '@material-ui/icons/PrintOutlined';
import { useReactToPrint } from 'react-to-print';
import { useEffect, useRef, useState } from 'react';
import './Print/print.scss';
import PrintComponent from './Print/PrintComponent';
import { generateDPReportPrintLog, getDataToPrint } from './Print/utils';
import Events from '../../../../../utilities/EventEmitter';
import GanttTooltip from "./GanttTooltip";
import { withDynamicStyles } from "./WithDynamicStyles";

const styles = (theme) => ({
  gridCardContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: 100,
    padding: 8,
    cursor: "pointer",
  },
  driverDetails: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: -6,
  },
  driverNameIconContainer: {
    display: "flex",
    alignItems: "center",
    color: theme.iconColor,
    fontSize: 12,
  },
  driverIcon: {
    fontSize: 30,
  },
  driverName: {
    fontWeight: 600,
    lineHeight: 3,
    marginLeft: 4,
  },
  cardActions: {
    display: "flex",
    width: 80,
    justifyContent: "flex-end",
    marginRight: 12,
  },
  actionIcon: {
    cursor: "pointer",
    color: theme.iconColor,
    pointerEvents: "none",
  },
  warningIcon: {
    color: "#F5A623",
  },
  driverStatusContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: 32,
  },
  statusIconDelayed: {
    color: colors.delayedColor,
    fontSize: 12,
  },
  statusIconOnTime: {
    color: colors.primaryAppColor,
    fontSize: 12,
  },
  statusIndicatorIcon: { display: "flex", marginLeft: 28, marginTop: 8 },
  margin: {
    margin: 0,
  },
  customBadge: {
    color: "#C9830E",
    backgroundColor: "#ffffff",
    boxShadow: "0px 1px 6px rgb(0 0 0 / 20%)",
    height: "14px",
    minWidth: "14px",
    borderRadius: "7px",
    fontSize: 10,
    transform: "scale(1) translate(13%, 2%)",
    fontWeight: 700,
    lineHeight: 2,
  },
  statusLabel: {
    lineHeight: 1,
    fontSize: 10,
    fontWeight: 600,
    marginLeft: 6,
  },
  delayedRow: {
    color: colors.delayedColor,
  },
  onTimeRow: {
    color: colors.primaryAppColor,
  },
  trialerTypeIconContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: 28,
    marginTop: 6,
  },
  iconClass: {
    color: theme.iconColor,
  },
  dateStyle: {
    marginTop: 8,
    alignSelf: "flex-start",
    marginLeft: 32,
    lineHeight: 1,
    fontSize: 10,
    fontWeight: 600,
    color: theme.iconColor,
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  printIcon: {
    color: theme.iconColor,
  },
});

const getClientCodeLists = (DispatchStore) => {
  if (!DispatchStore) return {};
  return {
    returnCodeList: DispatchStore.returnCodes || [],
    exceptionList: DispatchStore.exceptions || [],
  };
};

const DriverColumnCard = ({
  data,
  classes,
  DispatchStore,
  enableReturnExceptionReasonLabels = false
}) => {
  const { driverRoutesData } = data;
  const containerRef = useRef();
  const promiseResolveRef = useRef(null);
  const [printData, setPrintData] = useState([]);
  const clientCodeLists = getClientCodeLists(DispatchStore);

  useEffect(() => {
    if (printData && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [printData]);

  const totalReturns = driverRoutesData.reduce((acc, route) => {
    if (route.numberOfReturnShipments) {
      return acc + route.numberOfReturnShipments;
    }
    return acc;
  }, 0);

  const handlePrintClick = (e) => {
    e.stopPropagation();
    if (driverRoutesData.length > 1) {
      Events.emit('openPrintDialog', {
        driverRoutesData,
        driverData: data,
        clientCodeLists,
        enableReturnExceptionReasonLabels,
      });
      return;
    }
    handlePrint();
  };

  const onBeforeGetContent = () => {
    generateDPReportPrintLog(
      data.driverNumber,
      driverRoutesData.map((route) => route.routeId)
    );
    const dataToPrint = getDataToPrint(
      driverRoutesData,
      data,
      clientCodeLists,
      enableReturnExceptionReasonLabels
    );
    return new Promise((resolve) => {
      promiseResolveRef.current = resolve;
      setPrintData(dataToPrint);
    });
  };

  const handlePrint = useReactToPrint({
    content: () => containerRef.current,
    onBeforeGetContent: onBeforeGetContent,
    copyStyles: true,
    documentTitle : "Driver Performance Report"
  });

  return (
    <>
      <div className={classes.gridCardContainer}>
        <div id="driverDetails" className={classes.driverDetails}>
          <div className={classes.driverNameIconContainer}>
            <AccountCircleOutlined className={classes.driverIcon} />
            <span className={classes.driverName}>{`${data.text} (${data.driverNumber})`}</span>
          </div>
          <div className={classes.cardActions}>
            <div className={classes.actionContainer}>
              <Print className={classes.printIcon} onClick={handlePrintClick} />
              {totalReturns ? (
                <GanttTooltip
                  disableFocusListener
                  placement="top"
                  title={pluralize(totalReturns, 'RETURN')}>
                  <span>
                    <Badge
                      className={classes.margin}
                      classes={{ badge: classes.customBadge }}
                      badgeContent={totalReturns}>
                      <WarningRounded className={`${classes.warningIcon} ${classes.actionIcon}`} />
                    </Badge>
                  </span>
                </GanttTooltip>
              ) : null}
            </div>
          </div>
        </div>
        <div id="driverStatus" className={classes.driverStatusContainer}>
          {data.isRealtimeReport && (
            <>
              <FiberManualRecord
                className={
                  data.isDriverDelayed ? classes.statusIconDelayed : classes.statusIconOnTime
                }
              />
              <span
                className={`${classes.statusLabel} ${
                  data.isDriverDelayed ? classes.delayedRow : classes.onTimeRow
                }`}>
                {data.isDriverDelayed ? 'Behind Schedule' : 'On Track'}
              </span>
            </>
          )}
        </div>
        {data.isLiftGateRequired && (
          <div id="trailerType" className={classes.trialerTypeIconContainer}>
            <GanttTooltip placement="top" disableFocusListener title="LG">
              <i className={`icon-liftgate ${classes.iconClass}`} />
            </GanttTooltip>
          </div>
        )}
        {!data.isRealtimeReport && (
          <span className={classes.dateStyle}>
            {format(data.dispatchDate, reportConstants.REPORT_DISPLAY_DATE_FORMAT)}
          </span>
        )}
      </div>
      <PrintComponent containerRef={containerRef} printData={printData} driverData={data} />
    </>
  );
};

export default compose(withDynamicStyles(styles))(DriverColumnCard);
